import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { shopService } from '../services/shopService';
import { EyeIcon, XCircleIcon, CheckCircleIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import Pagination from '../../../components/common/Pagination';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import DeleteConfirmation from '../../../components/common/DeleteConfirmation';
import StarRating from './StarRating';
import ShopFilters from './ShopFilters';
import { ClockIcon } from 'lucide-react';
import { baseURL } from '../../../lib/axios';

const ShopList = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const navigate = useNavigate();
  
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['shops', page, filters],
    queryFn: () => shopService.getShops({ page, ...filters }),
  });
  
  const toggleStatusMutation = useMutation({
    mutationFn: (id) => shopService.toggleShopStatus(id),
    onSuccess: () => {
      refetch();
      setShowStatusModal(false);
    }
  });

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <div className="text-red-500">Erreur lors du chargement des boutiques</div>;

  const shops = data?.data || [];
  const pagination = data?.meta || {};
  
  const handleViewDetails = (id) => {
    navigate(`/shops/${id}`);
  };
  
  const handleToggleStatus = (shop) => {
    setSelectedShop(shop);
    setShowStatusModal(true);
  };
  
  const handleStatusConfirm = () => {
    if (selectedShop) {
      toggleStatusMutation.mutate(selectedShop.id);
    }
  };

  const statusPill = (status) => {
    const map = {
      pending: { t: "En attente", c: "bg-amber-50 text-amber-700" },
      approved: { t: "Ouvert", c: "bg-green-50 text-green-700" },
      rejected: { t: "Rejeté", c: "bg-red-50 text-red-700" },
    };
    const it = map[status] || { t: "Fermée", c: "bg-gray-100 text-gray-600" };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${it.c}`}>
        {it.t}
      </span>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <div>
          <h2 className="text-base font-medium text-gray-800">Boutiques</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {pagination.total ?? shops.length} boutique(s) au total.
          </p>
        </div>
      </div>

      <ShopFilters filters={filters} onFilterChange={handleFilterChange} />

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Boutique</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Propriétaire</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Produits</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Commandes</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Note</th>
              <th className="px-4 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Statut</th>
              <th className="px-4 py-3 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shops.length > 0 ? (
              shops.map(shop => (
                <tr key={shop.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {shop.logo ? (
                        <img src={`${baseURL}/storage/${shop.logo}`} alt="" className="h-9 w-9 rounded-full object-cover" />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center">
                          <ShoppingBagIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-800 flex items-center gap-1">
                        {shop.name}
                        {shop.is_verified && (
                          <ShieldCheckIcon className="h-4 w-4 text-blue-500" title="Boutique certifiée" />
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{shop.user?.firstname} {shop.user?.lastname}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{shop.products_count}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{shop.orders_count}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <StarRating rating={Number(shop.average_rating)} />
                      <span className="text-gray-500 text-xs ml-1">{Number(shop.average_rating || 0).toFixed(1)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{statusPill(shop.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        title="Voir les détails"
                        className="p-2 hover:bg-gray-100 rounded-md cursor-pointer"
                        onClick={() => handleViewDetails(shop.id)}
                      >
                        <EyeIcon className="h-5 w-5 text-gray-500" />
                      </button>
                      <button
                        title={
                          shop.status === 'pending' ? "Approuver" :
                          shop.status === 'approved' ? "Fermer la boutique" :
                          "Approuver / réactiver"
                        }
                        className="p-2 rounded-md cursor-pointer hover:bg-gray-100"
                        onClick={() => handleToggleStatus(shop)}
                      >
                        {shop.status === 'approved' ? (
                          <XCircleIcon className="h-5 w-5 text-red-500" />
                        ) : shop.status === 'pending' ? (
                          <ClockIcon className="h-5 w-5 text-amber-500" />
                        ) : (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-sm text-gray-500">
                  Aucune boutique trouvée
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />
      
      {/* Modal de confirmation */}
      <DeleteConfirmation
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleStatusConfirm}
        title={
          selectedShop?.status === 'pending' ? "Traiter la demande" :
          selectedShop?.status === 'approved' ? "Fermer la boutique" :
          selectedShop?.status === 'rejected' ? "Approuver la boutique" :
          "Réactiver la boutique"
        }
        message={
          selectedShop?.status === 'pending' ? `Êtes-vous sûr de vouloir traiter la demande pour ${selectedShop?.name} ?` :
          selectedShop?.status === 'approved' ? `Êtes-vous sûr de vouloir fermer la boutique ${selectedShop?.name} ?` :
          selectedShop?.status === 'rejected' ? `Êtes-vous sûr de vouloir approuver la boutique ${selectedShop?.name} ?` :
          `Êtes-vous sûr de vouloir réactiver la boutique ${selectedShop?.name} ?`
        }
        confirmButtonText={
          selectedShop?.status === 'pending' ? "Approuver" :
          selectedShop?.status === 'approved' ? "Fermer" :
          selectedShop?.status === 'rejected' ? "Approuver" :
          "Réactiver"
        }
        confirmButtonColor={
          selectedShop?.status === 'pending' ? "warning" :
          selectedShop?.status === 'approved' ? "danger" : // Fermer la boutique = action critique (rouge/danger)
          selectedShop?.status === 'rejected' ? "success" : // Approuver = succès (vert)
          "success"
        }
      />
    </div>
  );
};

export default ShopList;